import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Bell, CheckCircle2, Info, AlertTriangle, Megaphone } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { FeatureLocked } from "@/components/FeatureLocked";

const iconFor = (type: string) => {
  if (type === "success") return { Icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-500/15" };
  if (type === "warning") return { Icon: AlertTriangle, color: "text-amber-700", bg: "bg-amber-500/15" };
  if (type === "campaign") return { Icon: Megaphone, color: "text-gold", bg: "bg-gold/15" };
  return { Icon: Info, color: "text-blue-700", bg: "bg-blue-500/15" };
};

export default function Notifications() {
  const [notes, setNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/customer/notifications").then((r) => setNotes(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const markRead = async (id: string) => {
    await api.post(`/customer/notifications/${id}/read`);
    setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const markAllRead = async () => {
    await api.post("/customer/notifications/read-all");
    setNotes((prev) => prev.map((n) => ({ ...n, read: true })));
  };
  const hasUnread = notes.some((n) => !n.read);

  const ctx = useOutletContext<any>();
  const feat = ctx?.features?.notifications;
  if (feat && !feat.available) return <FeatureLocked title="Notificaciones" unlockTier={feat.unlock_tier} enabled={feat.enabled} />;

  if (loading)
    return <div className="flex justify-center h-96 items-center"><Loader2 className="w-7 h-7 text-gold animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif font-medium">Notificaciones</h1>
          <p className="text-sm text-slate-500 mt-1">Comunicaciones y alertas de tu programa Armony.</p>
        </div>
        {hasUnread && (
          <button
            onClick={markAllRead}
            className="text-sm text-gold-ink hover:underline shrink-0 mt-2"
            data-testid="notifications-read-all"
          >
            Marcar todas como leídas
          </button>
        )}
      </div>

      {notes.length === 0 ? (
        <div className="card-obsidian p-12 text-center">
          <Bell className="w-10 h-10 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500">No tienes notificaciones.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((n) => {
            const { Icon, color, bg } = iconFor(n.type);
            return (
              <div
                key={n.id}
                data-testid="notification-item"
                onClick={() => !n.read && markRead(n.id)}
                className={`card-obsidian p-4 flex gap-4 cursor-pointer ${!n.read ? "border-gold/30" : ""}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${bg}`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">{n.title}</h3>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-gold" />}
                    {n.channel === "whatsapp" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700">WhatsApp</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{n.message}</p>
                  <p className="text-xs text-slate-600 mt-1.5">
                    {new Date(n.created_at).toLocaleString("es", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "America/Guayaquil" })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
